import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUp, TrendingDown,
  AlertCircle, CheckCircle, Printer,
  Wallet, CreditCard, RefreshCw, MinusCircle, X,
  ChevronDown, ChevronRight, ChevronLeft, Edit2, Trash2, Coins
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { formatApiError } from '../utils/apiErrors';
import UiDateRangeBlock from '../components/ui/UiDateRangeBlock';
import { UiViewToggle } from '../components/ui/UiTabs';
import UiTimeField from '../components/ui/UiTimeField';
import SortableHeader from '../components/SortableHeader';
import useSortable from '../hooks/useSortable';
import { buildStandardPresetOptions, resolveStandardStartEdit, shiftDateRangeByPreset, shouldKeepStandardPresetForEnd } from '../utils/dateRangeUi';
import { formatUiCurrencyCompact, formatUiCurrencyDetailed, formatUiDate, formatUiDateTimeRange } from '../utils/uiFormat';

const formatDateValue = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const fmtDateRecon = (ds) => formatUiDate(ds);
const formatCurrencyCompact = (value) => formatUiCurrencyCompact(value);
const formatCurrencyDetailed = (value) => formatUiCurrencyDetailed(value);
const formatCurrency = (value) => formatUiCurrencyDetailed(value);

const fmtK = (n) => {
  const v = parseFloat(n || 0);
  if (Math.abs(v) >= 1000000) return (v/1000000).toFixed(1).replace(/\.0$/,'') + 'M';
  if (Math.abs(v) >= 1000) return (v/1000).toFixed(1).replace(/\.0$/,'') + 'K';
  return v.toFixed(0);
};

const getBusinessDate = (businessDayStartHour = 6) => {
  const now = new Date();
  const businessNow = new Date(now);
  if (now.getHours() < businessDayStartHour) {
    businessNow.setDate(businessNow.getDate() - 1);
  }
  return businessNow;
};

const getCurrentTimeValue = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const addCalendarDays = (dateStr, days) => {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return formatDateValue(date);
};

const getWeekRange = (businessDate) => {
  const date = new Date(`${businessDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return { s: businessDate, e: businessDate };
  const dayOffset = (date.getDay() + 6) % 7;
  const start = new Date(date);
  start.setDate(date.getDate() - dayOffset);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { s: formatDateValue(start), e: formatDateValue(end) };
};

const getWithdrawalTimeValue = (withdrawal) => {
  if (withdrawal?.withdrawal_time) return String(withdrawal.withdrawal_time).substring(0, 5);
  if (withdrawal?.created_at) {
    const d = new Date(withdrawal.created_at);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
  }
  return getCurrentTimeValue();
};

const formatWithdrawalTime = (withdrawal) => getWithdrawalTimeValue(withdrawal) || '--:--';

export const shouldLoadTillCounts = (selectedDate, reconView) => Boolean(selectedDate && reconView === 'detail');

export const getTillCountDifferenceDisplay = (value) => {
  const difference = Number(value || 0);
  if (difference > 0) return { label: 'Sales Entries Missing', className: 'text-green-600' };
  if (difference < 0) return { label: 'Cash Missing', className: 'text-red-600' };
  return { label: 'Balanced', className: 'text-gray-500' };
};

function DailyReconciliation() {
  const [businessDayStartHour, setBusinessDayStartHour] = useState(6);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => formatDateValue(getBusinessDate(6)));
  const [reportData, setReportData] = useState(null);
  const [cashRegister, setCashRegister] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [showOpenRegisterModal, setShowOpenRegisterModal] = useState(false);
  const [openingCashAmount, setOpeningCashAmount] = useState('');
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalData, setWithdrawalData] = useState({ id: null, amount: '', reason: '', withdrawal_time: getCurrentTimeValue() });
  const [withdrawals, setWithdrawals] = useState([]);
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);
  const [editOpeningAmount, setEditOpeningAmount] = useState('');
  const [showReconcileDetails, setShowReconcileDetails] = useState(false);
  const [reconcileData, setReconcileData] = useState({
    closing_cash_actual: '',
    next_opening_cash: '',
    variance_reason: '',
    notes: ''
  });

  const { isAdmin } = useAuthStore();
  const adminUser = isAdmin();
  const todayBusinessDate = useMemo(
    () => formatDateValue(getBusinessDate(businessDayStartHour)),
    [businessDayStartHour]
  );
  const selectedDateIsHistorical = selectedDate < todayBusinessDate;
  const selectedDateIsFuture = selectedDate > todayBusinessDate;
  const todayDetailOnly = !adminUser;
  const canSeeSales = adminUser || cashRegister?.is_reconciled || showReconcileDetails;
  const canShowDetailCards = adminUser || cashRegister?.is_reconciled || showReconcileDetails;
  const canShowVariance = adminUser;
  // Sorting for the reconciliation range table (default '' keeps chronological order).
  const { sortKey: rSortKey, sortDir: rSortDir, handleSort: rHandleSort, sortData: rSortData } = useSortable('', 'asc');
  const reconRowValue = (ds, key, reg) => {
    switch (key) {
      case 'date': return ds;
      case 'status': return reg?.is_reconciled ? 2 : reg ? 1 : 0;
      case 'opened_by': return reg?.opened_by_name || '';
      case 'opening_cash': return reg ? parseFloat(reg.opening_cash || 0) : -Infinity;
      case 'variance': return getVarianceValue(reg) ?? -Infinity;
      default: return ds;
    }
  };
  const canCreateOpeningCash = !selectedDateIsFuture && !cashRegister && (adminUser || !selectedDateIsHistorical);
  const canEditOpeningCash = !!cashRegister && !selectedDateIsFuture && adminUser;
  const canUseOpeningCash = canCreateOpeningCash || canEditOpeningCash;
  const canAddWithdrawal = !selectedDateIsFuture && !!cashRegister && (adminUser || (!selectedDateIsHistorical && !cashRegister?.is_reconciled));
  const canManageWithdrawals = adminUser && !selectedDateIsFuture && !!cashRegister;
  const canReconcileDay = !selectedDateIsFuture && !!cashRegister && (adminUser || (!selectedDateIsHistorical && !cashRegister?.is_reconciled));
  const [expandedCats, setExpandedCats] = useState({});
  const [reconView, setReconView] = useState('detail');
  const [reconLastListView, setReconLastListView] = useState('calendar');
  const [reconPreset, setReconPreset] = useState('today');
  const [reconCustomStart, setReconCustomStart] = useState('');
  const [reconCustomEnd, setReconCustomEnd] = useState('');
  const [reconRangeOverride, setReconRangeOverride] = useState(null);
  const [reconCalMonthYM, setReconCalMonthYM] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });
  const [reconList, setReconList] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [tillCounts, setTillCounts] = useState([]);
  const [showTillCounts, setShowTillCounts] = useState(false);
  const [showCashCountModal, setShowCashCountModal] = useState(false);
  const [cashCountAmount, setCashCountAmount] = useState('');
  const [savingCashCount, setSavingCashCount] = useState(false);
  const [cashCountInterval, setCashCountInterval] = useState(0);
  const [editTillId, setEditTillId] = useState(null);
  const [editTillAmount, setEditTillAmount] = useState('');

  useEffect(() => {
    api.get('/settings/').then((res) => {
      const nextHour = res.data?.business_day_start_hour ?? 6;
      setBusinessDayStartHour(nextHour);
      setCashCountInterval(res.data?.cash_count_interval_minutes || 0);
      setSelectedDate(formatDateValue(getBusinessDate(nextHour)));
    }).catch(() => {});
    api.get('/public-holidays/?page_size=500').then((res) => {
      setPublicHolidays(res.data.results || res.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!todayDetailOnly) return;
    if (selectedDate !== todayBusinessDate) setSelectedDate(todayBusinessDate);
    if (reconView !== 'detail') setReconView('detail');
  }, [todayDetailOnly, selectedDate, todayBusinessDate, reconView]);

  useEffect(() => {
    if (reconView === 'detail') fetchData();
  }, [selectedDate, reconView]);

  // Staff and admins both need the latest count time in the Enter Cash popup. Editing/history
  // controls remain admin-gated elsewhere in the page.
  const loadTillCounts = () => {
    if (!selectedDate) { setTillCounts([]); return; }
    api.get(`/till-cash-counts/?business_date=${selectedDate}&page_size=200`)
      .then((res) => setTillCounts(res.data.results || res.data || []))
      .catch(() => setTillCounts([]));
  };
  useEffect(() => {
    if (!shouldLoadTillCounts(selectedDate, reconView)) { setTillCounts([]); return; }
    loadTillCounts();
  }, [selectedDate, reconView]);

  // Enter current till cash (same forced-count entry the POS shows on interval expiry).
  // Available to admin and staff: recording a count resets the POS forced-count timer,
  // letting staff pre-empt the modal before busy hours.
  const saveCashCount = async () => {
    const amt = parseFloat(cashCountAmount);
    if (Number.isNaN(amt) || amt < 0) { toast.error('Enter the total cash in the till'); return; }
    setSavingCashCount(true);
    try {
      await api.post('/till-cash-counts/', { amount: amt, counted_at: new Date().toISOString() });
      setShowCashCountModal(false);
      setCashCountAmount('');
      toast.success('Till cash recorded. The counter has been reset.');
      loadTillCounts();
    } catch (e) {
      toast.error('Failed to record till cash count');
    } finally {
      setSavingCashCount(false);
    }
  };

  const saveTillEdit = async (id) => {
    const amt = parseFloat(editTillAmount);
    if (Number.isNaN(amt) || amt < 0) { toast.error('Enter a valid amount'); return; }
    try {
      await api.patch(`/till-cash-counts/${id}/`, { amount: amt });
      setEditTillId(null); setEditTillAmount('');
      toast.success('Till count updated');
      loadTillCounts();
    } catch (e) { toast.error('Failed to update till count'); }
  };

  const deleteTillCount = async (id) => {
    if (!window.confirm('Delete this till count record?')) return;
    try {
      await api.delete(`/till-cash-counts/${id}/`);
      toast.success('Till count deleted');
      loadTillCounts();
    } catch (e) { toast.error('Failed to delete till count'); }
  };

  // Last recorded count + when the next forced count is due (interval from settings).
  const tillCountStatus = useMemo(() => {
    if (!tillCounts.length) return null;
    const last = tillCounts.reduce((a, b) => (new Date(a.counted_at) >= new Date(b.counted_at) ? a : b));
    const lastAt = new Date(last.counted_at);
    const fmt = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    if (!cashCountInterval) return `Last counted at ${fmt(lastAt)}`;
    const due = new Date(lastAt.getTime() + cashCountInterval * 60000);
    return `Last counted at ${fmt(lastAt)} · next due ${fmt(due)}`;
  }, [tillCounts, cashCountInterval]);

  useEffect(() => {
    setShowReconcileDetails(false);
  }, [selectedDate]);

  useEffect(() => {
    if (todayDetailOnly) return;
    const handlePop = () => {
      if (reconView !== 'detail') return;
      setReconView(reconLastListView || 'calendar');
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [reconLastListView, reconView, todayDetailOnly]);

  const reconRange = useMemo(() => {
    if (reconRangeOverride) return reconRangeOverride;
    const today = formatDateValue(getBusinessDate(businessDayStartHour));
    if (reconPreset === 'today') return { s: selectedDate || today, e: selectedDate || today };
    if (reconPreset === 'week') return getWeekRange(today);
    if (reconPreset === 'month') { const d = new Date(); return { s: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`, e: formatDateValue(new Date(d.getFullYear(), d.getMonth() + 1, 0)) }; }
    if (reconPreset === 'year') { const d = new Date(); return { s: `${d.getFullYear()}-01-01`, e: `${d.getFullYear()}-12-31` }; }
    if (reconPreset === 'custom') return { s: reconCustomStart, e: reconCustomEnd };
    return { s: today, e: today };
  }, [reconPreset, reconCustomStart, reconCustomEnd, businessDayStartHour, reconRangeOverride, selectedDate]);
  const reconMode = reconView === 'calendar' ? 'calendar' : 'table';
  const reconPresetOptions = reconMode === 'calendar'
    ? buildStandardPresetOptions(['month', 'year'])
    : buildStandardPresetOptions(['today', 'week', 'month', 'year', 'custom']);
  const reconStartTime = `${String(businessDayStartHour).padStart(2, '0')}:00`;
  const reconEndTime = `${String((businessDayStartHour + 23) % 24).padStart(2, '0')}:59`;
  const reconFilterEndCalendarDate = reconRange.e ? addCalendarDays(reconRange.e, 1) : '';

  useEffect(() => {
    if (reconMode !== 'calendar' || ['month', 'year'].includes(reconPreset)) return;
    setReconRangeOverride(null);
    setReconPreset('month');
  }, [reconMode, reconPreset]);

  const registerByDate = useMemo(() => {
    const map = {};
    reconList.forEach(r => { map[r.date] = r; });
    return map;
  }, [reconList]);

  const holidayMap = useMemo(() => {
    const map = {};
    publicHolidays.forEach((holiday) => {
      const start = holiday.start_date;
      const end = holiday.end_date || holiday.start_date;
      if (!start) return;
      let cursor = new Date(`${start}T12:00:00`);
      const endDate = new Date(`${end}T12:00:00`);
      while (!Number.isNaN(cursor.getTime()) && cursor <= endDate) {
        map[formatDateValue(cursor)] = holiday;
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [publicHolidays]);

  const getHolidayForDate = (dateStr) => holidayMap[dateStr] || null;
  const getVarianceValue = (register) => {
    if (!register || !register.is_reconciled || register.variance == null) return null;
    return parseFloat(register.variance || 0);
  };

  const changeReconCalMonth = (delta) => {
    const [yr, mo] = reconCalMonthYM.split('-').map(Number);
    const d = new Date(yr, mo - 1 + delta, 1);
    const newYm = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const firstDay = `${newYm}-01`;
    const lastDate = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const lastDay = `${newYm}-${String(lastDate).padStart(2,'0')}`;
    setReconCalMonthYM(newYm);
    setReconRangeOverride({ s: firstDay, e: lastDay });
    setReconPreset('month');
  };

  const openReconListView = (nextView = reconLastListView || 'calendar') => {
    const normalizedView = nextView === 'table' ? 'table' : 'calendar';
    if (normalizedView === 'calendar' && ['today', 'week'].includes(reconPreset)) {
      setReconPreset('month');
      setReconRangeOverride(null);
    }
    setReconLastListView(normalizedView);
    setReconView(normalizedView);
  };

  const setReconViewMode = (nextMode) => {
    if (todayDetailOnly) return;
    if (nextMode === 'calendar') {
      openReconListView('calendar');
      return;
    }
    setReconLastListView('table');
    if (reconPreset === 'today') {
      setReconView('detail');
      return;
    }
    setReconView('table');
  };

  const openReconDetail = (dateStr) => {
    if (!todayDetailOnly && reconView !== 'detail') {
      setReconLastListView(reconView === 'table' ? 'table' : 'calendar');
      window.history.pushState({ reconDetail: true }, '');
    }
    setSelectedDate(dateStr);
    setReconView('detail');
  };

  const handleReconPresetChange = (nextPreset) => {
    if (reconMode === 'calendar' && ['month', 'year'].includes(nextPreset)) {
      const [yr, mo] = reconCalMonthYM.split('-').map(Number);
      const base = new Date(yr, (mo || 1) - 1, 1);
      const rangeStart = nextPreset === 'year'
        ? `${base.getFullYear()}-01-01`
        : `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-01`;
      const rangeEnd = nextPreset === 'year'
        ? `${base.getFullYear()}-12-31`
        : `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
      setReconPreset(nextPreset);
      setReconRangeOverride({ s: rangeStart, e: rangeEnd });
      setReconCalMonthYM(rangeStart.substring(0, 7));
      openReconListView('calendar');
      return;
    }
    setReconPreset(nextPreset);
    setReconRangeOverride(null);
    if (nextPreset === 'today') {
      if (!todayDetailOnly && reconView !== 'detail') {
        setReconLastListView(reconView === 'table' ? 'table' : 'calendar');
        window.history.pushState({ reconDetail: true }, '');
      }
      setSelectedDate(todayBusinessDate);
      setReconView('detail');
      return;
    }

    if (nextPreset === 'custom' && (!reconCustomStart || !reconCustomEnd)) {
      const monthStart = `${todayBusinessDate.substring(0, 7)}-01`;
      setReconCustomStart(monthStart);
      setReconCustomEnd(todayBusinessDate);
      setReconCalMonthYM(monthStart.substring(0, 7));
    }

    if (!todayDetailOnly && reconView === 'detail') {
      openReconListView(nextPreset === 'week' ? 'table' : (reconLastListView || 'calendar'));
    }
  };

  const shiftReconRange = (delta) => {
    const shifted = shiftDateRangeByPreset({
      preset: reconPreset,
      startDate: reconRange.s,
      endDate: reconRange.e,
      delta,
    });
    const nextRange = { s: shifted.startDate, e: shifted.endDate };
    setReconRangeOverride(nextRange);
    if (reconPreset === 'today') {
      setSelectedDate(nextRange.s);
      setReconView('detail');
    } else {
      if (reconPreset === 'custom') {
        setReconCustomStart(nextRange.s);
        setReconCustomEnd(nextRange.e);
      }
      setReconCalMonthYM(nextRange.s.substring(0, 7));
    }
  };

  const handleReconCalendarPeriodChange = ({ mode, startDate, endDate }) => {
    setReconPreset(mode);
    setReconRangeOverride({ s: startDate, e: endDate });
    setReconCalMonthYM(startDate.substring(0, 7));
    openReconListView('calendar');
  };

  const fetchReconRangeData = async (rangeStart = reconRange.s, rangeEnd = reconRange.e) => {
    if (todayDetailOnly) return;
    if (!rangeStart || !rangeEnd) return;
    setRangeLoading(true);
    try {
      const res = await api.get(`/cash-registers/?start_date=${rangeStart}&end_date=${rangeEnd}&page_size=500`);
      const list = res.data.results || res.data;
      setReconList(Array.isArray(list) ? list : []);
    } catch (error) {
      setReconList([]);
      toast.error(formatApiError(error, 'Failed to fetch reconciliation history'));
    } finally {
      setRangeLoading(false);
    }
  };

  useEffect(() => {
    if (todayDetailOnly) return;
    if (reconView === 'detail') return;
    const { s, e } = reconRange;
    if (!s || !e) return;
    setReconList([]);
    fetchReconRangeData(s, e);
    setReconCalMonthYM(s.substring(0, 7));
  }, [todayDetailOnly, reconView, reconRange.s, reconRange.e]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch reconciliation report
      const reportRes = await api.get(`/reports/reconciliation/?date=${selectedDate}`);
      setReportData(reportRes.data);
      
      // Fetch cash register for the date
      try {
        const registerRes = await api.get(`/cash-registers/?date=${selectedDate}`);
        const registers = registerRes.data.results || registerRes.data;
        const nextRegister = registers.length > 0 ? registers[0] : null;
        setCashRegister(nextRegister);
        if (!todayDetailOnly) {
          setReconList(prev => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const index = next.findIndex(entry => entry.date === selectedDate);
            if (!nextRegister) return index >= 0 ? next.filter(entry => entry.date !== selectedDate) : next;
            if (index >= 0) next[index] = nextRegister;
            else next.push(nextRegister);
            return next;
          });
        }
      } catch (e) {
        setCashRegister(null);
      }

      // Fetch cash withdrawals for the date
      try {
        const wRes = await api.get(`/cash-withdrawals/?date=${selectedDate}`);
        setWithdrawals(wRes.data.results || wRes.data);
      } catch (e) {
        setWithdrawals([]);
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to fetch reconciliation data'));
    } finally {
      setLoading(false);
    }
  };

  const openRegister = async () => {
    if (!openingCashAmount || isNaN(openingCashAmount)) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      const response = await api.post('/cash-registers/open_register/', {
        date: selectedDate,
        opening_cash: parseFloat(openingCashAmount)
      });
      setCashRegister(response.data);
      setShowOpenRegisterModal(false);
      setOpeningCashAmount('');
      toast.success('Opening cash saved');
      await fetchData();
      await fetchReconRangeData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to open register'));
    }
  };

  const handleReconcile = async () => {
    if (!cashRegister) return;
    if (!reconcileData.closing_cash_actual || isNaN(reconcileData.closing_cash_actual)) {
      toast.error('Please enter actual cash in hand');
      return;
    }
    if (!reconcileData.next_opening_cash || isNaN(reconcileData.next_opening_cash)) {
      toast.error('Please enter the opening cash available for the next day');
      return;
    }
    if (!window.confirm(`Confirm cash left for next day as ${formatCurrency(reconcileData.next_opening_cash)}?`)) {
      return;
    }
    
    try {
      const response = await api.post(`/cash-registers/${cashRegister.id}/reconcile/`, {
        closing_cash_actual: parseFloat(reconcileData.closing_cash_actual),
        next_opening_cash: parseFloat(reconcileData.next_opening_cash),
        variance_reason: reconcileData.variance_reason,
        notes: reconcileData.notes
      });
      setCashRegister(response.data);
      setShowReconcileDetails(true);
      setShowReconcileModal(false);
      toast.success('Day reconciled successfully');
      await fetchData();
      await fetchReconRangeData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to reconcile'));
    }
  };

  const handleEditOpeningBalance = async () => {
    if (!editOpeningAmount || isNaN(editOpeningAmount)) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await api.patch(`/cash-registers/${cashRegister.id}/`, {
        opening_cash: parseFloat(editOpeningAmount)
      });
      setShowOpeningBalanceModal(false);
      setEditOpeningAmount('');
      toast.success('Opening balance updated');
      await fetchData();
      await fetchReconRangeData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update opening balance'));
    }
  };

  const handleWithdrawal = async () => {
    if (!withdrawalData.amount || isNaN(withdrawalData.amount)) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      const payload = {
        amount: parseFloat(withdrawalData.amount),
        reason: withdrawalData.reason,
        withdrawal_time: withdrawalData.withdrawal_time || getCurrentTimeValue(),
        date: selectedDate
      };
      if (withdrawalData.id) {
        await api.patch(`/cash-withdrawals/${withdrawalData.id}/`, payload);
      } else {
        await api.post('/cash-withdrawals/', payload);
      }
      setWithdrawalData({ id: null, amount: '', reason: '', withdrawal_time: getCurrentTimeValue() });
      toast.success(withdrawalData.id ? 'Cash withdrawal updated' : 'Cash withdrawal recorded');
      await fetchData();
      await fetchReconRangeData();
    } catch (error) {
      toast.error(formatApiError(error, withdrawalData.id ? 'Failed to update withdrawal' : 'Failed to record withdrawal'));
    }
  };

  const handleDeleteWithdrawal = async (withdrawal) => {
    if (!window.confirm('Delete this cash withdrawal?')) return;
    try {
      await api.delete(`/cash-withdrawals/${withdrawal.id}/`);
      toast.success('Cash withdrawal deleted');
      await fetchData();
      await fetchReconRangeData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete withdrawal'));
    }
  };

  const openOpeningCashEntry = () => {
    if (cashRegister) {
      if (!canEditOpeningCash) return;
      setEditOpeningAmount(cashRegister.opening_cash || '0');
      setShowOpeningBalanceModal(true);
    } else {
      if (!canCreateOpeningCash) return;
      setOpeningCashAmount('');
      setShowOpenRegisterModal(true);
    }
  };

  const openWithdrawalEntry = (withdrawal = null) => {
    if (!cashRegister) return;
    if (withdrawal && !canManageWithdrawals) return;
    if (!withdrawal && !canAddWithdrawal) return;
    setWithdrawalData(withdrawal
      ? { id: withdrawal.id, amount: withdrawal.amount || '', reason: withdrawal.reason || '', withdrawal_time: getWithdrawalTimeValue(withdrawal) }
      : { id: null, amount: '', reason: '', withdrawal_time: getCurrentTimeValue() }
    );
    setShowWithdrawalModal(true);
  };

  const openClosingCashEntry = () => {
    if (!canReconcileDay || !cashRegister) return;
    const nextOpeningExists = !!reportData?.cash_reconciliation?.next_day_opening_exists;
    const nextOpeningValue = nextOpeningExists
      ? reportData?.cash_reconciliation?.next_day_opening_cash
      : cashRegister.opening_cash;
    setReconcileData({
      closing_cash_actual: cashRegister.closing_cash_actual || '',
      next_opening_cash: nextOpeningValue ?? '',
      variance_reason: cashRegister.variance_reason || '',
      notes: cashRegister.notes || '',
    });
    setShowReconcileModal(true);
  };

  const printReport = () => {
    window.print();
  };

  if (loading && reconView === 'detail') {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const variance = reportData?.final_summary?.variance != null ? parseFloat(reportData.final_summary.variance || 0) : null;
  const isPositiveVariance = variance == null ? true : variance >= 0;
  const varianceDisplay = variance == null ? null : getTillCountDifferenceDisplay(variance);
  const openingCash = parseFloat(reportData?.cash_reconciliation?.opening_cash || 0);
  const totalCashSales = parseFloat(reportData?.payment_breakdown?.cash_sales || reportData?.cash_reconciliation?.cash_sales || 0);
  const totalWithdrawals = parseFloat(reportData?.cash_reconciliation?.total_withdrawals || 0);
  const nextDayOpeningCash = parseFloat(reportData?.cash_reconciliation?.next_day_opening_cash || 0);
  const totalExpectedCash = parseFloat(reportData?.cash_reconciliation?.total_expected_cash || reportData?.final_summary?.expected_cash_in_hand || 0);
  const totalDigitalSales = parseFloat(reportData?.payment_breakdown?.digital_sales || 0);
  const totalSalesToday = parseFloat(reportData?.cash_reconciliation?.total_sales_today || (totalCashSales + totalDigitalSales));
  const availableCash = reportData?.final_summary?.actual_cash_in_hand != null
    ? parseFloat(reportData?.final_summary?.actual_cash_in_hand || 0)
    : null;
  const cashTakenForDayValue = parseFloat(reconcileData.closing_cash_actual || 0) || 0;
  const cashLeftForNextDayValue = parseFloat(reconcileData.next_opening_cash || 0) || 0;
  const reconcileCashSplitTotal = cashTakenForDayValue + cashLeftForNextDayValue;
  const sortedWithdrawals = [...withdrawals].sort((a, b) => String(getWithdrawalTimeValue(b)).localeCompare(String(getWithdrawalTimeValue(a))));
  const cashActionDisabledReason = selectedDateIsFuture
    ? 'Future reconciliation dates cannot be edited'
    : selectedDateIsHistorical && !adminUser
      ? 'Only admin and super admin can edit historical reconciliation data'
      : cashRegister?.is_reconciled && !adminUser
        ? 'Only admin and super admin can edit reconciled data'
        : '';
  const openingCashDisabledReason = selectedDateIsFuture
    ? 'Future reconciliation dates cannot be edited'
    : selectedDateIsHistorical && !adminUser
      ? 'Only admin and super admin can edit historical reconciliation data'
      : cashRegister && !adminUser
        ? 'Opening balance is locked after entry. Only admin and super admin can edit it.'
        : cashRegister?.is_reconciled && !adminUser
          ? 'Only admin and super admin can edit reconciled data'
          : '';
  const cashButtonBase = 'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="space-y-4 sm:space-y-6 print:p-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 print:mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Daily Reconciliation</h1>
          {reconView === 'detail'
            ? <p className="text-sm text-gray-500">Viewing: <strong>{fmtDateRecon(selectedDate)}</strong></p>
            : <p className="text-sm sm:text-base text-gray-500">End-of-day cash and sales reconciliation</p>}
        </div>
      </div>

      {adminUser && (
        <UiDateRangeBlock
          preset={reconPreset}
          presetOptions={reconPresetOptions}
          onPresetChange={handleReconPresetChange}
          onPrevious={() => shiftReconRange(-1)}
          onNext={() => shiftReconRange(1)}
          periodPicker={reconMode === 'calendar'}
          onPeriodChange={handleReconCalendarPeriodChange}
          fromDate={reconRange.s}
          onFromDateChange={(nextDate) => {
            const range = resolveStandardStartEdit({
              preset: reconPreset,
              nextStartDate: nextDate,
              startTime: reconStartTime,
              endTime: reconEndTime,
              businessDayStartHour,
            });
            if (range) {
              setReconRangeOverride({ s: range.startDate, e: range.endDate });
              setReconCalMonthYM(range.startDate.substring(0, 7));
              if (reconPreset === 'today') setSelectedDate(range.startDate);
              return;
            }
            setReconRangeOverride(null);
            setReconCustomStart(nextDate);
            setReconCustomEnd(reconCustomEnd || reconRange.e || nextDate);
            setReconPreset('custom');
          }}
          toDate={reconFilterEndCalendarDate}
          onToDateChange={(nextDate) => {
            const nextBusinessEnd = addCalendarDays(nextDate, -1);
            const keepPreset = shouldKeepStandardPresetForEnd({
              preset: reconPreset,
              startDate: reconRange.s,
              nextEndDate: nextBusinessEnd,
              startTime: reconStartTime,
              endTime: reconEndTime,
              businessDayStartHour,
            });
            if (keepPreset) {
              setReconRangeOverride({ s: reconRange.s, e: nextBusinessEnd });
              return;
            }
            setReconRangeOverride(null);
            setReconCustomStart(reconCustomStart || reconRange.s || nextBusinessEnd);
            setReconCustomEnd(nextBusinessEnd);
            setReconPreset('custom');
          }}
          fromTime={reconStartTime}
          toTime={reconEndTime}
          showTime
          timeDisabled
          rangeText={`Selected range: ${reconRange.s && reconFilterEndCalendarDate ? formatUiDateTimeRange(reconRange.s, reconStartTime, reconFilterEndCalendarDate, reconEndTime) : ''}`}
          header={(
            <div className="flex flex-wrap items-center gap-2">
              <UiViewToggle
                options={[{ value: 'calendar', label: 'Calendar' }, { value: 'table', label: 'Table' }]}
                value={reconMode}
                onChange={setReconViewMode}
              />
            </div>
          )}
        />
      )}

      {reconView === 'detail' && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-orange-100 bg-white p-3 shadow-sm print:hidden">
          <button
            onClick={openOpeningCashEntry}
            disabled={!canUseOpeningCash}
            title={cashRegister && !canEditOpeningCash ? openingCashDisabledReason || 'Opening cash cannot be edited now' : openingCashDisabledReason || 'Enter opening cash'}
            className={`${cashButtonBase} bg-blue-500 text-white hover:bg-blue-600`}
          >
            <span>Opening</span>
          </button>
          <button
            onClick={() => openWithdrawalEntry()}
            disabled={!canAddWithdrawal}
            title={!cashRegister ? 'Enter opening cash before withdrawing cash' : cashActionDisabledReason || 'Enter withdrawn cash'}
            className={`${cashButtonBase} bg-red-500 text-white hover:bg-red-600`}
          >
            <span>Withdraw</span>
          </button>
          <button
            onClick={openClosingCashEntry}
            disabled={!canReconcileDay}
            title={!cashRegister ? 'Enter opening cash before reconciling' : cashActionDisabledReason || 'Reconcile day'}
            className={`${cashButtonBase} bg-orange-600 text-white hover:bg-orange-700`}
          >
            <span>Reconcile</span>
          </button>
          {selectedDate === todayBusinessDate && (
            <button
              onClick={() => { setCashCountAmount(''); setShowCashCountModal(true); }}
              title="Count the till now and reset the forced cash-count timer"
              className={`${cashButtonBase} bg-teal-600 text-white hover:bg-teal-700`}
            >
              <span>Enter Cash</span>
            </button>
          )}
          <button onClick={printReport} className={`${cashButtonBase} bg-gray-100 text-gray-700 hover:bg-gray-200`}>
            <span>Print</span>
          </button>
        </div>
      )}

      {/* Calendar View */}
      {reconView === 'calendar' && (() => {
        const todayStr = todayBusinessDate;
        const MO_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        const MO_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

        /* ── Year Grid ─────────────────────────────────────── */
        if (reconPreset === 'year') {
          const calYr = new Date(reconRange.s || todayStr.substring(0,4) + '-01-01').getFullYear();
          const monthAgg = {};
          for (let m = 0; m < 12; m++) {
            const ms = `${calYr}-${String(m+1).padStart(2,'0')}`;
            const monthRegs = reconList.filter(r => String(r.date).substring(0,7) === ms);
            const reconciled = monthRegs.filter(r => r.is_reconciled).length;
            const pending = monthRegs.filter(r => !r.is_reconciled).length;
            const netVariance = monthRegs.filter(r => r.is_reconciled).reduce((s, r) => s + (getVarianceValue(r) || 0), 0);
            monthAgg[ms] = { reconciled, pending, netVariance, total: monthRegs.length };
          }
          return (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <span className="font-semibold text-gray-800 text-lg">{calYr} — Year Overview</span>
                {rangeLoading && <RefreshCw className="w-4 h-4 animate-spin text-orange-400" />}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {MO_SHORT.map((mName, mi) => {
                  const ms = `${calYr}-${String(mi+1).padStart(2,'0')}`;
                  const agg = monthAgg[ms] || { reconciled: 0, pending: 0, netVariance: 0, total: 0 };
                  const isFutureMonth = ms > todayStr.substring(0,7);
                  const firstDay = `${calYr}-${String(mi+1).padStart(2,'0')}-01`;
                  const lastDate = new Date(calYr, mi+1, 0).getDate();
                  const lastDay = `${calYr}-${String(mi+1).padStart(2,'0')}-${String(lastDate).padStart(2,'0')}`;
                  let bg = 'bg-gray-50 border-gray-200', txt = 'text-gray-400';
                  if (!isFutureMonth && agg.total > 0) {
                    if (agg.pending > 0) { bg = 'bg-yellow-50 border-yellow-300'; txt = 'text-yellow-800'; }
                    else { bg = 'bg-green-50 border-green-200'; txt = 'text-green-800'; }
                  }
                  return (
                    <div key={ms} onClick={() => { if (!isFutureMonth) { setReconRangeOverride({ s: firstDay, e: lastDay }); setReconPreset('month'); setReconCalMonthYM(ms); } }}
                      className={`${bg} border rounded-xl p-3 ${!isFutureMonth ? 'cursor-pointer hover:shadow-md transition' : 'opacity-40'}`}>
                      <div className={`font-bold text-sm ${txt}`}>{mName}</div>
                      {!isFutureMonth && agg.total > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {agg.reconciled > 0 && <div className="text-xs text-green-700">✓ {agg.reconciled}d</div>}
                          {agg.pending > 0 && <div className="text-xs text-yellow-700">⟳ {agg.pending}d</div>}
                          <div className={`text-xs font-semibold ${agg.netVariance >= 0 ? 'text-green-600' : 'text-red-500'}`}>{agg.netVariance >= 0 ? '+' : ''}{fmtK(agg.netVariance)}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        const [calYr, calMo] = reconCalMonthYM.split('-').map(Number);
        const daysInMonth = new Date(calYr, calMo, 0).getDate();
        const firstDow = new Date(calYr, calMo - 1, 1).getDay();
        const firstMon = firstDow === 0 ? 6 : firstDow - 1;
        const cells = [];
        for (let i = 0; i < firstMon; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
        return (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => changeReconCalMonth(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><ChevronLeft className="w-5 h-5" /></button>
              <span className="font-semibold text-gray-800">{MO_NAMES[calMo-1]} {calYr}</span>
              <button onClick={() => changeReconCalMonth(1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <div key={d} className="text-center text-xs text-gray-400 py-1">{d}</div>)}
            </div>
            {rangeLoading ? <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-orange-400" /></div> : (
              <div className="grid grid-cols-7 gap-1">
                {cells.map((day, i) => {
                  if (!day) return <div key={`e${i}`} />;
                  const ds = `${calYr}-${String(calMo).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const reg = registerByDate[ds];
                  const holiday = getHolidayForDate(ds);
                  const regVariance = getVarianceValue(reg);
                  const isToday = ds === todayStr;
                  const isFuture = ds > todayStr;
                  let bg = isFuture ? 'bg-gray-50 border-gray-100' : 'bg-gray-300 border-gray-400', txt = isFuture ? 'text-gray-500' : 'text-gray-600';
                  if (!isFuture) {
                    if (reg?.is_reconciled) { bg = 'bg-green-50 border-green-300'; txt = 'text-green-800'; }
                    else if (reg) { bg = 'bg-yellow-50 border-yellow-300'; txt = 'text-yellow-800'; }
                  }
                  if (holiday && !reg) {
                    bg = 'bg-amber-50 border-amber-300';
                    txt = 'text-amber-800';
                  }
                  return (
                    <div key={ds} title={fmtDateRecon(ds)} onClick={() => openReconDetail(ds)}
                      className={`${bg} border rounded-lg p-1.5 min-h-[54px] cursor-pointer hover:opacity-75 transition ${holiday ? 'ring-1 ring-amber-300' : ''} ${isToday ? 'ring-2 ring-orange-500' : ''}`}>
                      <div className={`text-xs font-bold ${txt}`}>{day}</div>
                      {holiday && <div className="text-[9px] text-amber-700 font-semibold leading-tight truncate">{holiday.name}</div>}
                      {!isFuture && reg && (
                        <div className="mt-0.5">
                          {reg.is_reconciled
                            ? (canShowVariance && regVariance != null
                                ? <div className={`text-xs leading-tight font-semibold ${regVariance >= 0 ? 'text-green-700' : 'text-red-600'}`}>{regVariance >= 0 ? '+' : ''}{fmtK(regVariance)}</div>
                                : <div className="text-xs text-green-700 leading-tight">✓ Reconciled</div>)
                            : <div className="text-xs text-yellow-700 leading-tight">⟳ Pending</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300 inline-block"/>Reconciled</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300 inline-block"/>Pending</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300 inline-block"/>Holiday</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-300 border border-gray-400 inline-block"/>Past No Activity</span>
              <span className="ml-auto text-gray-300">Click a date for details</span>
            </div>
          </div>
        );
      })()}

      {/* Table View */}
      {reconView === 'table' && (() => {
        const todayStr = todayBusinessDate;
        const { s: rs, e: re } = reconRange;
        const dates = [];
        if (rs && re) { let d = new Date(rs + 'T12:00:00'); const end = new Date(re + 'T12:00:00'); while (d <= end) { dates.push(formatDateValue(d)); d.setDate(d.getDate()+1); } }
        const reconciled = reconList.filter(r => r.is_reconciled).length;
        const pending = reconList.filter(r => !r.is_reconciled).length;
        return (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center gap-3 text-sm text-gray-500">
              <span className="font-medium text-gray-800">{dates.length} days</span>
              <span className="text-green-600">✓ {reconciled} reconciled</span>
              <span className="text-yellow-600">⟳ {pending} pending</span>
              {rangeLoading && <RefreshCw className="w-4 h-4 animate-spin text-orange-400 ml-auto" />}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
                  <tr>
                    <SortableHeader label="Date" col="date" sortKey={rSortKey} sortDir={rSortDir} onSort={rHandleSort} />
                    <SortableHeader label="Status" col="status" sortKey={rSortKey} sortDir={rSortDir} onSort={rHandleSort} />
                    <SortableHeader label="Opened By" col="opened_by" sortKey={rSortKey} sortDir={rSortDir} onSort={rHandleSort} />
                    <SortableHeader label="Opening Cash" col="opening_cash" sortKey={rSortKey} sortDir={rSortDir} onSort={rHandleSort} right />
                    {canShowVariance && <SortableHeader label="Variance" col="variance" sortKey={rSortKey} sortDir={rSortDir} onSort={rHandleSort} right />}
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rSortData(dates, (ds, key) => reconRowValue(ds, key, registerByDate[ds])).map(ds => {
                    const reg = registerByDate[ds];
                    const holiday = getHolidayForDate(ds);
                    const regVariance = getVarianceValue(reg);
                    const isFuture = ds > todayStr;
                    return (
                      <tr key={ds} onClick={() => openReconDetail(ds)} className={`${holiday ? 'bg-amber-50/70 hover:bg-amber-100' : 'hover:bg-orange-50'} cursor-pointer transition`}>
                        <td className="px-4 py-3 font-medium text-gray-800">{fmtDateRecon(ds)}</td>
                        <td className="px-4 py-3">
                          {reg?.is_reconciled ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Reconciled</span>
                            : reg ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">Pending</span>
                            : isFuture ? <span className="text-xs text-gray-300">Future</span>
                            : <span className="text-xs text-gray-400">No Activity</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{reg?.opened_by_name || '—'}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{reg ? formatCurrencyCompact(reg.opening_cash || 0) : '—'}</td>
                        {canShowVariance && (
                          <td className="px-4 py-3 text-right">
                            {reg?.is_reconciled && regVariance != null ? <span className={regVariance >= 0 ? 'text-green-600' : 'text-red-600'}>{formatCurrencyCompact(regVariance)}</span> : '—'}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right text-orange-500 text-xs">View →</td>
                      </tr>
                    );
                  })}
                  {dates.length === 0 && <tr><td colSpan={canShowVariance ? 6 : 5} className="px-4 py-8 text-center text-gray-400">No data for selected range</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {reconView === 'detail' && (<>
      {/* Report Title for Print */}
      <div className="hidden print:block text-center mb-6">
        <h1 className="text-xl font-bold">Daily Reconciliation Report</h1>
        <p className="text-gray-600">{fmtDateRecon(selectedDate)}</p>
      </div>

      {canShowDetailCards && (
      <>
      {/* Cash Register Status */}
      {cashRegister && (
        <div className={`p-3 sm:p-4 rounded-xl border-2 ${
          cashRegister.is_reconciled ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
        }`}>
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-3">Day Reconcile</h2>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div>
                <p className="font-semibold text-gray-800 text-sm sm:text-base">
                  {cashRegister.is_reconciled ? 'Day Reconciled' : 'Pending Reconciliation'}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  Opened by: {cashRegister.opened_by_name} | {formatCurrency(cashRegister.opening_cash)}
                </p>
              </div>
            </div>
            {cashRegister.is_reconciled && (
              <div className="text-left sm:text-right ml-9 sm:ml-0">
                <p className="text-xs sm:text-sm text-gray-600">By: {cashRegister.reconciled_by_name}</p>
                {canShowVariance && variance != null && (
                  <p className={`font-semibold ${isPositiveVariance ? 'text-green-600' : 'text-red-600'}`}>
                    Variance: {formatCurrency(variance)} · {varianceDisplay.label}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {false && (
        <>
        {/* Expenses Summary */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 order-1">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            
            Expenses Summary
          </h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Total Entries</span>
              <span className="font-semibold">{reportData?.expenses_summary?.expense_count || 0}</span>
            </div>
            
            {reportData?.expenses_summary?.by_category?.map((cat, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-dashed">
                <span className="text-gray-600">├─ {cat.category}</span>
                <span className="font-medium text-red-600">{formatCurrency(cat.total || 0)}</span>
              </div>
            ))}
            
            <div className="flex justify-between items-center py-3 bg-red-50 rounded-lg px-3 mt-4">
              <span className="font-semibold text-red-700">Total Expenses</span>
              <span className="text-xl font-bold text-red-700">
                {formatCurrency(reportData?.expenses_summary?.total_expenses || 0)}
              </span>
            </div>
          </div>
        </div>

        </>
        )}

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            Cash Reconciliation
          </h2>

          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-gray-100">
              <span className="text-xs text-gray-600">i. Opening Change</span>
              <span className="text-xs font-semibold">{formatCurrency(openingCash)}</span>
            </div>
            <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-green-50">
              <span className="text-xs text-gray-600">ii. Cash Sales</span>
              <span className="text-xs font-semibold">{formatCurrency(totalCashSales)}</span>
            </div>
            <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-red-50">
              <span className="text-xs text-gray-600">iii. Total Cash Withdrawal</span>
              <span className="text-xs font-semibold text-red-600">{formatCurrency(totalWithdrawals)}</span>
            </div>
            <div className="flex justify-between items-center py-2 px-3 rounded-lg bg-red-50">
              <span className="text-xs text-gray-600">iv. Change left for Tomorrow</span>
              <span className="text-xs font-semibold text-blue-700">{formatCurrency(nextDayOpeningCash)}</span>
            </div>

            <div className="flex justify-between items-center py-3 bg-orange-50 rounded-lg px-3">
              <span className="text-xs font-semibold text-orange-700">v. Expected Cash Balance <span className="text-[10px] text-orange-400 font-normal">(i + ii − iii − iv)</span></span>
              <span className="text-sm sm:text-base font-bold text-orange-700">{formatCurrency(totalExpectedCash)}</span>
            </div>

            <div className="rounded-lg overflow-hidden border border-gray-100">
              <button onClick={() => setShowTillCounts(v => !v)} className="w-full flex justify-between items-center py-2 px-3 hover:bg-gray-50 text-left">
                <span className="text-xs text-gray-600 flex items-center gap-1">vi. Available Cash {tillCounts.length > 0 && <span className="text-[10px] text-gray-400">({tillCounts.length} count{tillCounts.length !== 1 ? 's' : ''}) {showTillCounts ? '▲' : '▼'}</span>}</span>
                <span className="text-xs font-semibold">{availableCash != null ? formatCurrency(availableCash) : 'Not entered'}</span>
              </button>
              {showTillCounts && tillCounts.length > 0 && (
                <div className="bg-gray-50 divide-y divide-gray-100 border-t border-gray-100">
                  {tillCounts.map((c) => (
                    <div key={c.id} className="flex justify-between items-baseline px-4 py-1.5 text-[10px] gap-2">
                      <span className="text-gray-500 whitespace-nowrap">
                        {new Date(c.counted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} · {c.recorded_by_name || '—'}
                      </span>
                      <span className="text-right flex items-center gap-1.5">
                        {editTillId === c.id ? (
                          <>
                            <input type="number" min="0" step="0.01" autoFocus value={editTillAmount}
                              onChange={(e) => setEditTillAmount(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveTillEdit(c.id); if (e.key === 'Escape') setEditTillId(null); }}
                              className="w-20 px-1 py-0.5 border border-gray-300 rounded text-[10px] text-right" />
                            <button onClick={() => saveTillEdit(c.id)} className="text-teal-600 font-medium">Save</button>
                            <button onClick={() => { setEditTillId(null); setEditTillAmount(''); }} className="text-gray-400">Cancel</button>
                          </>
                        ) : (
                          <>
                            <span className="font-medium text-gray-700">{formatCurrency(c.amount)}</span>
                            {(c.expected_at_time != null || c.variance_at_time != null) && (
                              <span className="text-gray-400">
                                (Expected: {formatCurrency(c.expected_at_time || 0)}
                                {c.variance_at_time != null && (
                                  <span className={getTillCountDifferenceDisplay(c.variance_at_time).className}>
                                    {' · '}Diff: {c.variance_at_time > 0 ? '+' : ''}{formatCurrency(c.variance_at_time)} · {getTillCountDifferenceDisplay(c.variance_at_time).label}
                                  </span>
                                )})
                              </span>
                            )}
                            {adminUser && (
                              <>
                                <button onClick={() => { setEditTillId(c.id); setEditTillAmount(String(c.amount)); }} title="Edit" className="p-0.5 text-blue-500 hover:text-blue-700"><Edit2 size={12} /></button>
                                <button onClick={() => deleteTillCount(c.id)} title="Delete" className="p-0.5 text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                              </>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {canShowVariance && (
              <div className={`flex justify-between items-center py-3 rounded-lg px-3 ${
                variance == null ? 'bg-gray-50' : isPositiveVariance ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <span className={`text-xs font-semibold ${variance == null ? 'text-gray-600' : isPositiveVariance ? 'text-green-700' : 'text-red-700'}`}>Variance <span className="text-[10px] font-normal opacity-70">(vi − v)</span></span>
                <span className={`text-right text-base sm:text-lg font-bold ${variance == null ? 'text-gray-600' : isPositiveVariance ? 'text-green-700' : 'text-red-700'}`}>
                  <span className="block">{variance != null ? formatCurrency(variance) : 'Pending'}</span>
                  {varianceDisplay && <span className="block text-[10px] sm:text-xs font-semibold">{varianceDisplay.label}</span>}
                </span>
              </div>
            )}

            <div className="border-t-4 border-double border-gray-300 my-1" />

            <div className="flex justify-between items-center py-2 px-3">
              <span className="text-xs text-gray-600">vii. Total Digital/Bank Sale</span>
              <span className="text-xs font-semibold text-blue-700">{formatCurrency(totalDigitalSales)}</span>
            </div>

            <div className="flex justify-between items-center py-3 bg-green-50 rounded-lg px-3">
              <span className="text-xs font-semibold text-green-700">Total Sale of the Day <span className="text-[10px] text-green-500 font-normal">(ii + vii)</span></span>
              <span className="text-base sm:text-lg font-bold text-green-700">{formatCurrency(totalSalesToday)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Department-wise Breakdown */}
      {canSeeSales && reportData?.department_sales?.length > 0 && (
        <div className="grid grid-cols-1 gap-6">
          {reportData?.department_sales?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                Sales by Department
              </h2>
              <div className="space-y-2">
                {reportData.department_sales.map((ds, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="font-medium text-gray-700">{ds.department_name}</span>
                    <div className="text-right">
                      <span className="font-bold text-purple-700">{formatCurrency(ds.total || 0)}</span>
                      <span className="text-xs text-gray-500 ml-2">({ds.quantity} items)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {false && reportData?.department_expenses?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                Expenses by Department
              </h2>
              <div className="space-y-2">
                {reportData.department_expenses.map((de, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                    <span className="font-medium text-gray-700">{de.department_name}</span>
                    <span className="font-bold text-red-700">{formatCurrency(de.total || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Tree Breakdown */}
      {canSeeSales && reportData?.category_tree?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            
            Category Breakdown
          </h2>
          <div className="space-y-1">
            {reportData.category_tree.map((cat) => (
              <div key={cat.category_id}>
                <button
                  onClick={() => setExpandedCats(prev => ({ ...prev, [cat.category_id]: !prev[cat.category_id] }))}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition"
                >
                  <div className="flex items-center gap-2">
                    {expandedCats[cat.category_id] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <span className="font-medium text-gray-800">{cat.category_name}</span>
                    {cat.department_name && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{cat.department_name}</span>}
                  </div>
                  <span className="font-bold text-gray-700">{formatCurrency(cat.total || 0)}</span>
                </button>
                {expandedCats[cat.category_id] && (
                  <div className="ml-8 mb-2 space-y-1">
                    {cat.items?.map((item) => (
                      <div key={item.menu_item_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                        <div>
                          <span className="text-gray-700">{item.name}</span>
                          <span className="text-gray-400 ml-2">x{item.quantity}</span>
                        </div>
                        <div className="flex gap-4 text-right">
                          <span className="text-gray-600">{formatCurrency(Math.round(item.revenue || 0))}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      </>
      )}

      {/* Cash Withdrawal Modal */}
      {showWithdrawalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                
                {withdrawalData.id ? 'Edit Cash Withdrawal' : 'Withdraw Cash'}
              </h2>
              <button onClick={() => { setShowWithdrawalModal(false); setWithdrawalData({ id: null, amount: '', reason: '', withdrawal_time: getCurrentTimeValue() }); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (Rs.) *
                </label>
                <input
                  type="number"
                  value={withdrawalData.amount}
                  onChange={(e) => setWithdrawalData(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 text-lg"
                  placeholder="Enter amount..."
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <textarea
                  value={withdrawalData.reason}
                  onChange={(e) => setWithdrawalData(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  rows="2"
                  placeholder="Reason for withdrawal..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <UiTimeField
                  value={withdrawalData.withdrawal_time}
                  onChange={(nextTime) => setWithdrawalData(prev => ({ ...prev, withdrawal_time: nextTime }))}
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleWithdrawal}
                  disabled={!withdrawalData.amount || isNaN(withdrawalData.amount)}
                  className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {withdrawalData.id ? 'Update' : 'Apply'}
                </button>
              </div>

              <div className="border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                  <p className="text-sm font-semibold text-gray-700">Withdrawals</p>
                  <p className="text-sm font-bold text-red-600">
                    {formatCurrency(withdrawals.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0))}
                  </p>
                </div>
                {withdrawals.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">No cash withdrawals recorded for this day</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">Time</th>
                          <th className="px-3 py-2 text-left">Reason</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {sortedWithdrawals.map((w) => (
                          <tr key={w.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-gray-700">{formatWithdrawalTime(w)}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{w.reason || 'No reason'}</div>
                              <div className="text-xs text-gray-400">{w.withdrawal_number}</div>
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-red-600 whitespace-nowrap">
                              {formatCurrency(parseFloat(w.amount || 0))}
                            </td>
                            <td className="px-3 py-2">
                              {canManageWithdrawals && (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => setWithdrawalData({ id: w.id, amount: w.amount || '', reason: w.reason || '', withdrawal_time: getWithdrawalTimeValue(w) })}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-orange-50 hover:text-orange-600"
                                    title="Edit cash withdrawal"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteWithdrawal(w)}
                                    className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                                    title="Delete cash withdrawal"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2 border-t">
                <button
                  onClick={() => { setShowWithdrawalModal(false); setWithdrawalData({ id: null, amount: '', reason: '', withdrawal_time: getCurrentTimeValue() }); }}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Open Register Modal */}
      {showOpenRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              
              Opening Cash
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opening Cash Amount (Rs.) *
                </label>
                <input
                  type="number"
                  value={openingCashAmount}
                  onChange={(e) => setOpeningCashAmount(e.target.value)}
                  className="w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 text-lg"
                  placeholder="Enter opening cash..."
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowOpenRegisterModal(false); setOpeningCashAmount(''); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={openRegister}
                  disabled={!openingCashAmount || isNaN(openingCashAmount)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Save Opening Cash
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reconcile Modal */}
      {showReconcileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 sm:p-6 max-h-[90vh] overflow-auto">
            <h2 className="text-lg font-semibold mb-4">Reconcile - {fmtDateRecon(selectedDate)}</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taking for the Day (Rs.) *
                </label>
                <input
                  type="number"
                  value={reconcileData.closing_cash_actual}
                  onChange={(e) => setReconcileData(prev => ({ ...prev, closing_cash_actual: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter actual cash count"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Change left for Tomorrow (Rs.) *
                </label>
                <input
                  type="number"
                  value={reconcileData.next_opening_cash}
                  onChange={(e) => setReconcileData(prev => ({ ...prev, next_opening_cash: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="Enter next day opening cash"
                  required
                />
              </div>

              <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
                Cash Taken for the Day + Cash Left for Tomorrow: <span className="font-semibold">{formatCurrency(reconcileCashSplitTotal)}</span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Variance Reason (if any)
                </label>
                <textarea
                  value={reconcileData.variance_reason}
                  onChange={(e) => setReconcileData(prev => ({ ...prev, variance_reason: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="2"
                  placeholder="Explain any variance..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={reconcileData.notes}
                  onChange={(e) => setReconcileData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowReconcileModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReconcile}
                  disabled={!reconcileData.closing_cash_actual || !reconcileData.next_opening_cash}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  Confirm Reconcile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Opening Balance Modal */}
      {showOpeningBalanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">Opening Cash</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening Cash (Rs.)</label>
                <input
                  type="number"
                  value={editOpeningAmount}
                  onChange={(e) => setEditOpeningAmount(e.target.value)}
                  className="w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                  placeholder="Enter opening cash amount..."
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowOpeningBalanceModal(false); setEditOpeningAmount(''); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditOpeningBalance}
                  disabled={!editOpeningAmount || isNaN(editOpeningAmount)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Save Opening Cash
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enter Current Cash (same forced till-count entry as the POS) */}
      {showCashCountModal && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Count the Till</h3>
            <p className="text-sm text-gray-500 mb-1">Enter the total cash currently in the till. Recording it now resets the forced cash-count timer.</p>
            {tillCountStatus && <p className="text-xs text-gray-400 mb-3">{tillCountStatus}</p>}
            {!tillCountStatus && <div className="mb-3" />}
            <input
              type="number" autoFocus min="0" step="0.01"
              value={cashCountAmount}
              onChange={(e) => setCashCountAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCashCount(); }}
              placeholder="Total cash in till"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg mb-3 text-lg font-semibold"
            />
            <div className="flex gap-2">
              <button onClick={() => { setShowCashCountModal(false); setCashCountAmount(''); }} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium">Cancel</button>
              <button onClick={saveCashCount} disabled={savingCashCount} className="flex-1 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{savingCashCount ? 'Saving…' : 'Record'}</button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

export default DailyReconciliation;
